import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
import { Modal, Spin } from "antd";
import { toast, Toaster } from "react-hot-toast";
import Pagination from "components/pagination";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  getAdminReview,
  getReviewsByPartnerId,
  getServiceReviewSummary,
  updateAdminReviewStatus,
} from "../../reviews/reviewApi";
import {
  formatReviewDate,
  getCustomerName,
  getRatingValue,
  getReviewMedia,
  getReviewText,
  getServiceName,
  getStatusMeta,
  renderStarsText,
} from "../../reviews/reviewUtils";

const MEDIA_BASE_URL = process.env.REACT_APP_HAPS_MAIN_BASE_URL || "";

const resolveReviewMediaUrl = (path) => {
  if (!path) return "";

  const trimmedPath = String(path).trim();
  if (!trimmedPath) return "";
  if (/^https?:\/\//i.test(trimmedPath)) return trimmedPath;

  const normalizedBase = MEDIA_BASE_URL.replace(/\/+$/, "");
  const normalizedPath = trimmedPath.replace(/^\/+/, "");

  return normalizedBase ? `${normalizedBase}/${normalizedPath}` : normalizedPath;
};

const renderReviewMedia = (mediaItems = []) => {
  if (!Array.isArray(mediaItems) || mediaItems.length === 0) {
    return <span className="text-xs text-gray-400">No media</span>;
  }

  return (
    <div className="flex min-w-[120px] flex-wrap gap-2">
      {mediaItems.map((item, index) => {
        const mediaType = item?.type || item?.media_type;
        const mediaUrl = resolveReviewMediaUrl(item?.url || item?.media_url);
        const mediaLabel = mediaType === "video" ? `Video ${index + 1}` : `Media ${index + 1}`;

        if (!mediaUrl) return null;

        return (
          <a
            key={item?.id || mediaUrl}
            href={mediaUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-blue-600 hover:underline"
          >
            {mediaLabel}
          </a>
        );
      })}
    </div>
  );
};

const ServiceReviewsView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const antIcon = <LoadingOutlined style={{ fontSize: 50 }} spin />;

  const [loading, setLoading] = useState(false);
  const [service, setService] = useState(() =>
    location.state?.service && typeof location.state.service === "object" ? location.state.service : {}
  );
  const [summaryData, setSummaryData] = useState({});
  const [reviews, setReviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState("");
  const [partnerReviews, setPartnerReviews] = useState([]);
  const [partnerReviewsLoading, setPartnerReviewsLoading] = useState(false);

  const summaryMetrics = summaryData;

  const fetchServiceSummary = async (showLoader = true) => {
    if (!serviceId) return;
    if (showLoader) setLoading(true);

    try {
      const result = await getServiceReviewSummary(serviceId);

      if (result?.status === 200) {
        const payload =
          result?.data && typeof result.data === "object" && !Array.isArray(result.data)
            ? result.data
            : result;
        const summary = payload?.summary && typeof payload.summary === "object" ? payload.summary : {};
        const reviewRows = Array.isArray(payload?.reviews) ? payload.reviews : [];

        setSummaryData(summary);
        setReviews(reviewRows);
        setService((prev) =>
          summary?.service_id ? { ...prev, id: prev?.id || summary.service_id, service_id: summary.service_id } : prev
        );
      } else {
        toast.error(result?.message || "Failed to fetch service review summary");
      }
    } catch (error) {
      console.error("Error fetching service review summary:", error);
      toast.error("Failed to fetch service review summary");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceSummary();
  }, [serviceId]);

  const reviewStats = useMemo(() => {
    const total = reviews.length;
    const average = total
      ? reviews.reduce((sum, item) => sum + getRatingValue(item), 0) / total
      : 0;
    const approved = reviews.filter((item) => getStatusMeta(item?.status).label === "Approved").length;
    const pending = reviews.filter((item) => getStatusMeta(item?.status).label === "Pending").length;
    const withMedia = reviews.filter((item) => getReviewMedia(item).length > 0).length;

    return {
      total: Number(summaryMetrics?.total_reviews ?? summaryMetrics?.total ?? total) || total,
      average:
        Number(summaryMetrics?.average_rating ?? summaryMetrics?.avg_rating ?? summaryMetrics?.average ?? average) ||
        average,
      approved:
        Number(summaryMetrics?.approved_reviews ?? summaryMetrics?.approved ?? approved) || approved,
      pending: Number(summaryMetrics?.pending_reviews ?? summaryMetrics?.pending ?? pending) || pending,
      withMedia:
        Number(summaryMetrics?.with_media_reviews ?? summaryMetrics?.with_media ?? withMedia) || withMedia,
    };
  }, [reviews, summaryMetrics]);

  const filteredReviews = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return reviews;

    return reviews.filter((review) =>
      [
        review?.id,
        review?.customer_id,
        getCustomerName(review),
        getReviewText(review),
      ]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query))
    );
  }, [reviews, searchTerm]);

  const totalRecords = filteredReviews.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedReviews = filteredReviews.slice(startIndex, endIndex);
  const displayedCount = totalRecords === 0 ? 0 : Math.min(totalRecords, endIndex);
  const selectedReviewStatus = getStatusMeta(selectedReview?.status);

  const openReviewModal = async (review) => {
    if (!review?.id) return;

    setModalVisible(true);
    setSelectedReview(review);
    setPartnerReviews([]);
    setDetailLoading(true);

    try {
      const result = await getAdminReview(review.id);

      if (result?.status === 200 && result?.data && typeof result.data === "object") {
        setSelectedReview(result.data);
      } else if (result?.status !== 200) {
        toast.error(result?.message || "Failed to fetch review details");
      }
    } catch (error) {
      console.error("Error fetching review details:", error);
      toast.error("Failed to fetch review details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    if (!selectedReview?.id) return;

    setStatusLoading(status);

    try {
      const result = await updateAdminReviewStatus(selectedReview.id, status);

      if (result?.status === 200) {
        toast.success(result?.message || "Review status updated");
        await fetchServiceSummary(false);

        const detailResult = await getAdminReview(selectedReview.id);
        if (detailResult?.status === 200 && detailResult?.data && typeof detailResult.data === "object") {
          setSelectedReview(detailResult.data);
        } else {
          setSelectedReview((prev) => (prev ? { ...prev, status } : prev));
        }
      } else {
        toast.error(result?.message || "Failed to update review status");
      }
    } catch (error) {
      console.error("Error updating review status:", error);
      toast.error("Failed to update review status");
    } finally {
      setStatusLoading("");
    }
  };

  const loadPartnerReviews = async () => {
    const partnerId = selectedReview?.partner_id || selectedReview?.partner?.id;

    if (!partnerId) return;

    setPartnerReviewsLoading(true);

    try {
      const result = await getReviewsByPartnerId(partnerId);

      if (result?.status === 200) {
        setPartnerReviews(Array.isArray(result?.data) ? result.data : []);
      } else {
        toast.error(result?.message || "Failed to fetch partner reviews");
      }
    } catch (error) {
      console.error("Error fetching partner reviews:", error);
      toast.error("Failed to fetch partner reviews");
    } finally {
      setPartnerReviewsLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Toaster position="top-center" reverseOrder={false} />

      <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Service Reviews</h2>
            <p className="mt-1 text-sm text-gray-500">Reviews for selected service.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/service-reviews")}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Back
          </button>
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Spin indicator={antIcon} />
          </div>
        ) : (
          <>
            <div className="mb-5 grid grid-cols-1 gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4 md:grid-cols-4">
              <div>
                <p className="text-[11px] uppercase text-gray-500">Service ID</p>
                <p className="text-sm font-semibold text-gray-800">
                  {service?.service_id || service?.id || serviceId || "-"}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">Service Name</p>
                <p className="text-sm font-semibold text-gray-800">
                  {service?.name || service?.service_name || summaryData?.service_name || getServiceName(summaryData) || "-"}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">Type</p>
                <p className="text-sm font-semibold text-gray-800">
                  {service?.service_type_name || service?.type_name || summaryData?.service_type_name || summaryData?.type_name || "-"}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">Rate</p>
                <p className="text-sm font-semibold text-gray-800">
                  {service?.rate ?? summaryData?.rate ?? "-"}
                </p>
              </div>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4 md:grid-cols-5">
              <div>
                <p className="text-[11px] uppercase text-gray-500">Total Reviews</p>
                <p className="text-sm font-semibold text-gray-800">{reviewStats.total}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">Average Rating</p>
                <p className="text-sm font-semibold text-gray-800">
                  {reviewStats.average.toFixed(1)} / 5
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">Approved</p>
                <p className="text-sm font-semibold text-gray-800">{reviewStats.approved}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">Pending</p>
                <p className="text-sm font-semibold text-gray-800">{reviewStats.pending}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">With Media</p>
                <p className="text-sm font-semibold text-gray-800">{reviewStats.withMedia}</p>
              </div>
            </div>

            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <input
                type="text"
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 md:w-96"
                placeholder="Search reviews by customer or comment"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setCurrentPage(1);
                }}
              />

              <div className="w-full rounded-md border border-gray-100 md:w-auto">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  recordsPerPage={recordsPerPage}
                  onRecordsPerPageChange={(value) => {
                    setRecordsPerPage(value);
                    setCurrentPage(1);
                  }}
                  totalRecords={totalRecords}
                  count={displayedCount}
                  showFirstLastControls
                  compact
                />
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-left text-xs text-gray-600">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-700">
                  <tr>
                    <th className="px-3 py-2.5">#</th>
                    <th className="px-3 py-2.5">Customer</th>
                    <th className="px-3 py-2.5">Rating</th>
                    <th className="px-3 py-2.5">Comment</th>
                    <th className="px-3 py-2.5">Customer Media</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">Date</th>
                    <th className="px-3 py-2.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedReviews.length > 0 ? (
                    paginatedReviews.map((review, index) => {
                      const statusMeta = getStatusMeta(review?.status);

                      return (
                        <tr key={review?.id ?? index} className="border-b bg-white align-top hover:bg-gray-50">
                          <td className="px-3 py-2">{startIndex + index + 1}</td>
                          <td className="px-3 py-2 font-medium text-gray-800">
                            {getCustomerName(review) || "-"}
                          </td>
                          <td className="px-3 py-2 text-amber-600">
                            {renderStarsText(getRatingValue(review))}
                          </td>
                          <td className="max-w-[280px] px-3 py-2">{getReviewText(review) || "-"}</td>
                          <td className="px-3 py-2">{renderReviewMedia(getReviewMedia(review))}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`rounded px-2 py-1 text-[10px] font-semibold uppercase ${statusMeta.className}`}
                            >
                              {statusMeta.label}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2">
                            {formatReviewDate(review?.created_at)}
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => openReviewModal(review)}
                              className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="py-6 text-center text-sm text-gray-500">
                        No reviews found for this service.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 rounded-md border border-gray-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                recordsPerPage={recordsPerPage}
                onRecordsPerPageChange={(value) => {
                  setRecordsPerPage(value);
                  setCurrentPage(1);
                }}
                totalRecords={totalRecords}
                count={displayedCount}
                showFirstLastControls
                compact
              />
            </div>
          </>
        )}
      </div>

      <Modal
        title={`Review Details${selectedReview?.id ? ` #${selectedReview.id}` : ""}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={780}
      >
        {detailLoading ? (
          <div className="flex h-48 items-center justify-center">
            <Spin indicator={antIcon} />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4 md:grid-cols-2">
              <div>
                <p className="text-[11px] uppercase text-gray-500">Customer</p>
                <p className="text-sm font-semibold text-gray-800">
                  {getCustomerName(selectedReview) || "-"}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">Service</p>
                <p className="text-sm font-semibold text-gray-800">
                  {getServiceName(selectedReview) || service?.name || "-"}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">Created At</p>
                <p className="text-sm font-semibold text-gray-800">
                  {formatReviewDate(selectedReview?.created_at, true)}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">Rating</p>
                <p className="pt-1 text-sm font-semibold text-amber-600">
                  {renderStarsText(getRatingValue(selectedReview))}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-gray-500">Status</p>
                <span
                  className={`mt-1 inline-flex rounded px-2 py-1 text-[10px] font-semibold uppercase ${selectedReviewStatus.className}`}
                >
                  {selectedReviewStatus.label}
                </span>
              </div>
            </div>

            <div>
              <p className="text-[11px] uppercase text-gray-500">Review</p>
              <p className="mt-1 rounded-lg border border-gray-100 bg-white p-3 text-sm text-gray-700">
                {getReviewText(selectedReview) || "-"}
              </p>
            </div>

            <div>
              <p className="mb-2 text-[11px] uppercase text-gray-500">Customer Media</p>
              {renderReviewMedia(getReviewMedia(selectedReview))}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleStatusUpdate("1")}
                disabled={statusLoading === "1"}
                className="rounded bg-green-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
              >
                {statusLoading === "1" ? "Updating..." : "Approve"}
              </button>
              <button
                type="button"
                onClick={() => handleStatusUpdate("2")}
                disabled={statusLoading === "2"}
                className="rounded bg-red-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
              >
                {statusLoading === "2" ? "Updating..." : "Reject"}
              </button>
              {(selectedReview?.partner_id || selectedReview?.partner?.id) && (
                <button
                  type="button"
                  onClick={loadPartnerReviews}
                  disabled={partnerReviewsLoading}
                  className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 disabled:opacity-60"
                >
                  {partnerReviewsLoading ? "Loading partner reviews..." : "Load Partner Reviews"}
                </button>
              )}
            </div>

            {partnerReviews.length > 0 && (
              <div>
                <p className="mb-2 text-[11px] uppercase text-gray-500">Partner Reviews</p>
                <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-100">
                  {partnerReviews.map((reviewItem) => {
                    const partnerStatus = getStatusMeta(reviewItem?.status);

                    return (
                      <div
                        key={reviewItem?.id}
                        className="border-b border-gray-100 px-3 py-2 last:border-b-0"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-medium text-gray-800">
                            #{reviewItem?.id || "-"} {getCustomerName(reviewItem) || ""}
                          </p>
                          <span
                            className={`rounded px-2 py-1 text-[10px] font-semibold uppercase ${partnerStatus.className}`}
                          >
                            {partnerStatus.label}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-600">{getReviewText(reviewItem) || "-"}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
};

export default ServiceReviewsView;
